import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errText, money } from "../../lib/api";
import { Btn, F, PageHeader, Toggle } from "../../components/admin/ui";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { ConfirmDelete } from "./Products";

const EMPTY = { code: "", type: "percent", value: 10, min_subtotal: 0, max_uses: 0, expires_at: "", active: true };

export default function Coupons() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/coupons").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...edit, value: Number(edit.value), min_subtotal: Number(edit.min_subtotal || 0), max_uses: Number(edit.max_uses || 0), expires_at: edit.expires_at ? new Date(edit.expires_at).toISOString() : null };
    try {
      if (edit.id) await api.put(`/admin/coupons/${edit.id}`, body);
      else await api.post("/admin/coupons", body);
      toast.success("تم حفظ الكوبون");
      setEdit(null);
      load();
    } catch (e) {
      const m = { code_exists: "الكود مستخدم مسبقًا", invalid_code: "الكود: 3-20 حرف إنجليزي أو رقم", invalid_value: "قيمة غير صالحة" };
      toast.error(m[e?.response?.data?.detail] || errText(e));
    }
  };

  return (
    <div data-testid="admin-coupons">
      <PageHeader title="الكوبونات والخصومات">
        <Btn data-testid="add-coupon-button" onClick={() => setEdit(EMPTY)}><Plus size={16} />كوبون جديد</Btn>
      </PageHeader>
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500"><tr className="text-right"><th className="p-3">الكود</th><th>الخصم</th><th>الحد الأدنى</th><th>الاستخدام</th><th>الانتهاء</th><th>الحالة</th><th></th></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} data-testid={`coupon-row-${c.code}`} className="border-t border-gray-100">
                <td className="p-3 font-mono font-bold">{c.code}</td>
                <td>{c.type === "percent" ? `${c.value}%` : `${money(c.value)} ر.س`}</td>
                <td>{money(c.min_subtotal)}</td>
                <td>{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("ar-SA") : "—"}</td>
                <td><span className={`rounded px-2 py-0.5 text-xs ${c.active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{c.active ? "مفعل" : "متوقف"}</span></td>
                <td className="p-3"><div className="flex justify-end gap-1">
                  <button onClick={() => setEdit({ ...c, expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "" })} className="rounded p-1.5 hover:bg-gray-100"><Pencil size={15} /></button>
                  <ConfirmDelete testid={`delete-coupon-${c.code}`} onConfirm={async () => { await api.delete(`/admin/coupons/${c.id}`); load(); }} />
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogTitle>{edit?.id ? "تعديل الكوبون" : "كوبون جديد"}</DialogTitle>
          {edit && (
            <div className="grid grid-cols-2 gap-3">
              <F label="الكود" dir="ltr" testid="coupon-code-input" className="col-span-2" value={edit.code} onChange={(v) => setEdit({ ...edit, code: v.toUpperCase() })} />
              <div className="col-span-2 flex gap-2">
                {[["percent", "نسبة %"], ["fixed", "مبلغ ثابت"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setEdit({ ...edit, type: k })} className={`flex-1 rounded border py-2 text-sm ${edit.type === k ? "border-ink bg-ink text-white" : "border-gray-300"}`}>{l}</button>
                ))}
              </div>
              <F label="القيمة" type="number" testid="coupon-value-input" value={edit.value} onChange={(v) => setEdit({ ...edit, value: v })} />
              <F label="الحد الأدنى للطلب" type="number" value={edit.min_subtotal} onChange={(v) => setEdit({ ...edit, min_subtotal: v })} />
              <F label="أقصى عدد استخدام (0 = غير محدود)" type="number" value={edit.max_uses} onChange={(v) => setEdit({ ...edit, max_uses: v })} />
              <F label="تاريخ الانتهاء" type="date" value={edit.expires_at} onChange={(v) => setEdit({ ...edit, expires_at: v })} />
              <div className="col-span-2"><Toggle label="مفعل" checked={edit.active} onChange={(v) => setEdit({ ...edit, active: v })} /></div>
              <Btn data-testid="save-coupon-button" className="col-span-2" onClick={save} disabled={!edit.code || !edit.value}>حفظ</Btn>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
